import { BetaBadge } from "@/components/ui/beta-badge";
import { blue, FaroText, grey } from "@faro-lotv/flat-ui";
import { Card, Stack } from "@mui/material";
import { Box } from "@mui/system";
import type { JSX } from "react";

interface Props {
  /** The icon to display at the top of the card. */
  icon: JSX.Element;
  /** The title of the card. */
  title: string;
  /** The description of the card. */
  description: string | JSX.Element;
  /** A footer line, e.g. for allowed file extensions. */
  footer?: JSX.Element;
  /** Aria label for the card for identification in tests. */
  ariaLabel?: string;
  /** Whether to show a "Beta" badge next to the title. */
  hasBetaBadge?: boolean;
  /** Callback when the card is clicked. */
  onClick(): void;
}

/**
 * @returns Card used to display an import option. Used in:
 *  - Import dialog opened by the "Import" button
 *  - Screen shown for an empty project and a user with write access.
 */
export function ImportCard({
  icon,
  title,
  description,
  footer,
  ariaLabel,
  hasBetaBadge,
  onClick,
}: Props): JSX.Element {
  const centerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  return (
    <Card
      aria-label={ariaLabel}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 2,
        border: `1px solid ${blue[100]}`,
        borderRadius: 3,
        boxShadow: "none",
        ":hover": {
          borderColor: blue[500],
          cursor: "pointer",
        },
      }}
      onClick={onClick}
    >
      {/* Icon */}
      <Box
        component="div"
        sx={{
          ...centerStyle,
          width: "80px",
          height: "80px",
          border: `3px solid ${blue[100]}`,
          borderRadius: "50%",
          color: blue[500],
          backgroundColor: blue[50],
          mx: "auto",
          mt: 0.5,
          mb: 3,
        }}
      >
        {icon}
      </Box>

      {/* Title */}
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 0.75 }}>
        <FaroText variant="heading16">{title}</FaroText>
        {hasBetaBadge && <BetaBadge />}
      </Stack>

      {/* Description */}
      <FaroText
        variant="bodyM"
        sx={{ mb: 2, color: grey[600] }}
        component="div"
      >
        {description}
      </FaroText>

      {/* Footer */}
      {footer ? (
        <Box component="div" sx={{ alignContent: "end", flexGrow: 1 }}>
          {footer}
        </Box>
      ) : null}
    </Card>
  );
}
